/**
 * De vormen die de API teruggeeft, gedeeld tussen Worker en scherm.
 *
 * Eén beschrijving van elke vorm: wat de Worker samenstelt is precies wat het
 * scherm leest. Geen bedragen die het scherm zelf zou moeten uitrekenen.
 */

import type { Niveau, Vooruitzicht } from './niveau';
import type { Heldstatus } from './status';
import type { Rol } from './rollen';

export type Heldsoort = 'persoon' | 'bedrijf';
export type Bijdragesoort = 'jaarbijdrage' | 'afkoop_zilver' | 'afkoop_goud';
export type Bijdragestatus = 'open' | 'betaald' | 'mislukt' | 'verlopen' | 'kwijtgescholden';
export type Betaalwijze = 'online' | 'incasso' | 'handmatig';

export const SOORTNAMEN: Record<Bijdragesoort, string> = {
  jaarbijdrage: 'Jaarbijdrage',
  afkoop_zilver: 'Afkoop zilver',
  afkoop_goud: 'Afkoop goud',
};

export const BIJDRAGESTATUSNAMEN: Record<Bijdragestatus, string> = {
  open: 'Open',
  betaald: 'Betaald',
  mislukt: 'Mislukt',
  verlopen: 'Vervallen',
  kwijtgescholden: 'Kwijtgescholden',
};

export const WIJZENAMEN: Record<Betaalwijze, string> = {
  online: 'Online (iDEAL)',
  incasso: 'Incasso',
  handmatig: 'Handmatig',
};

export interface Bijdrageregel {
  id: string;
  soort: Bijdragesoort;
  /** Het volgnummer (1, 2, 3 …) waar het niveau uit volgt. */
  heldenjaar: number | null;
  /** Het kalenderjaar dat deze jaarbijdrage dekt. */
  kalenderjaar: number | null;
  periode_van: string | null;
  periode_tot: string | null;
  bedrag_cent: number;
  status: Bijdragestatus;
  wijze: Betaalwijze | null;
  mollie_payment_id: string | null;
  betaald_op: string | null;
  aangekondigd_op: string | null;
  herinnerd_op: string | null;
  toelichting: string | null;
  aangemaakt_op: string;
  betaal_token?: string;
}

/** Wat een held op de muur is: niet meer dan dit (SPEC §6.2). */
export interface Muurheld {
  weergavenaam: string;
  regel: string | null;
  niveau: Niveau;
  sinds: number;
  anoniem: boolean;
  soort: Heldsoort;
  /** Alleen bij een bedrijf met logo. */
  logo_url: string | null;
  website: string | null;
  /** 5, 10, 15 …: dit jaar een jubileum (SPEC §6.5). */
  jubileum: number | null;
}

export interface Muur {
  helden: Muurheld[];
  aantal: number;
  oudsteJaren: number;
  perNiveau: Record<Niveau, number>;
  tarieven: {
    jaarbijdrage_cent: number;
    jaarbijdrage_bedrijf_cent: number;
  };
  /** Totaalprijs van een niveau voor een persoon, inclusief het lopende jaar. */
  afkoop: { zilver_totaal_cent: number; goud_totaal_cent: number };
  drempels: { zilverNaJaren: number; goudNaJaren: number };
  /** Is er een betaalkoppeling? Zonder kan aanmelden (nog) niet. */
  betalen_mogelijk?: boolean;
}

/** De held zoals hij zichzelf ziet op Mijn pagina (SPEC §6.1). */
export interface Heldstand {
  id: string;
  soort: Heldsoort;
  naam: string;
  email: string;
  telefoon: string | null;
  weergavenaam: string;
  regel: string | null;
  website: string | null;
  heeft_logo: boolean;
  toon_op_muur: boolean;
  status: Heldstatus;
  lid_sinds: string | null;
  afgekocht: Niveau | null;
  niveau: Niveau;
  betaaldeJaren: number;
  jubileum: number | null;
  volgend: Vooruitzicht | null;
  /** Het zelfgekozen jaarbedrag (null = het tarief) en het tarief zelf. */
  jaarbijdrage_cent: number | null;
  tarief_cent: number;
  volgendeIncasso: { datum: string; bedrag_cent: number } | null;
  mandaat: { tenaamstelling: string | null; laatste4: string | null } | null;
  bijdragen: Bijdrageregel[];
  afkoop: { niveau: Niveau; bedrag_cent: number }[];
  opgezegd_op: string | null;
}

export interface Heldoverzichtsregel {
  id: string;
  soort: Heldsoort;
  naam: string;
  email: string;
  weergavenaam: string;
  toon_op_muur: boolean;
  status: Heldstatus;
  lid_sinds: string | null;
  niveau: Niveau;
  betaaldeJaren: number;
  heeft_mandaat: boolean;
  jaarbijdrage_cent: number | null;
  open_cent: number;
  laatste_betaling_op: string | null;
  aangemaakt_op: string;
}

export interface Helddossier extends Heldstand {
  notitie_intern: string | null;
  factuuradres: string | null;
  opzegreden: string | null;
  mollie_customer_id: string | null;
  mollie_mandate_id: string | null;
  publieke_token: string;
  aangemaakt_op: string;
  logboek: Logregel[];
}

export interface Aandachtspunt {
  soort: 'incasso_mislukt' | 'bijdrage_open' | 'aanmelding_zonder_betaling' | 'geen_mandaat';
  held_id: string;
  naam: string;
  email: string;
  omschrijving: string;
  sinds: string;
  bedrag_cent: number | null;
  bijdrage_id: string | null;
}

export interface Beheeroverzicht {
  aandacht: Aandachtspunt[];
  cijfers: {
    actief: number;
    perNiveau: Record<Niveau, number>;
    bedrijven: number;
    ditJaarOntvangenCent: number;
    verwachtKomendJaarCent: number;
    nieuwDitJaar: number;
    opgezegdDitJaar: number;
  };
  komendeIncassos: { held_id: string; naam: string; datum: string; bedrag_cent: number; mandaat: boolean }[];
}

export interface Logregel {
  id: number;
  op: string;
  soort: string;
  door: string;
  held_id: string | null;
  held_naam: string | null;
  details: unknown;
}

export interface Logbladzijde {
  regels: Logregel[];
  totaal: number;
  soorten: { soort: string; aantal: number }[];
}

export interface Gebruikersregel {
  id: string;
  naam: string;
  email: string;
  rollen: Rol[];
  actief: boolean;
  aangemaakt_op: string;
  laatst_ingelogd_op: string | null;
}

export interface OmgevingInfo {
  testmodus: boolean;
  /** Draait op localhost: dan mag je zonder mailbox inloggen (testlogin). */
  lokaal: boolean;
  gebruiker: { naam: string; rollen: Rol[]; email: string } | null;
  heldIngelogd: boolean;
  klok: { verzet: boolean; datum: string } | null;
}

export interface Betaalgegevens {
  weergavenaam: string;
  soort: Bijdragesoort;
  kalenderjaar: number | null;
  periode_van: string | null;
  periode_tot: string | null;
  bedrag_cent: number;
  status: Bijdragestatus;
  betaald_op: string | null;
  rekening: { iban: string; tenaamstelling: string } | null;
  onlineMogelijk: boolean;
}

/** Een logo zoals het formulier het meestuurt: base64 in JSON. */
export interface Logo {
  type: string;
  data: string;
}

export interface Aanmeldinvoer {
  soort?: Heldsoort;
  naam: string;
  email: string;
  telefoon?: string;
  weergavenaam?: string;
  regel?: string;
  website?: string;
  logo?: Logo | null;
  toon_op_muur: boolean;
  afkoop?: Niveau | null;
  /** Zelfgekozen jaarbedrag; leeg = het tarief. Nooit lager dan het tarief. */
  bedrag_cent?: number | null;
  akkoord: boolean;
}

export interface Bedanktstand {
  status: 'wacht' | 'betaald' | 'mislukt';
  weergavenaam: string | null;
  niveau: Niveau | null;
  publieke_token: string | null;
}

export interface Voorbeeldmail {
  titel: string;
  wanneer: string;
  aan: string;
  onderwerp: string;
  tekst: string;
}

/** Een bericht aan de helden vanuit het beheer (SPEC §7.7). */
export interface Mailing {
  id: string;
  onderwerp: string;
  tekst: string;
  knop_tekst: string | null;
  knop_url: string | null;
  doelgroep: Doelgroep;
  verstuurd_op: string | null;
  verstuurd_door: string | null;
  aantal_ontvangers: number | null;
  aangemaakt_door: string;
  aangemaakt_op: string;
}

export interface Doelgroep {
  niveaus?: Niveau[];
  soorten?: Heldsoort[];
}

export interface Mailinginvoer {
  onderwerp: string;
  tekst: string;
  knop_tekst?: string | null;
  knop_url?: string | null;
  doelgroep?: Doelgroep;
}

// ---------------------------------------------------------------------------
// Facturen (SPEC §7.8)
// ---------------------------------------------------------------------------

/** Een vastgelegde factuur in de lijst van het dossier (zonder de PDF). */
export interface Factuurregel {
  id: string;
  nummer: string;
  datum: string;
  bijdrage_id: string | null;
  ontvanger_naam: string;
  ontvanger_adres: string | null;
  ontvanger_email: string;
  /** Samenvatting: de regels achter elkaar. */
  omschrijving: string;
  /** Het totaal. */
  bedrag_cent: number;
  regels: { omschrijving: string; bedrag_cent: number; bijdrage_id: string | null }[];
  verstuurd_op: string | null;
  verstuurd_door: string | null;
  aangemaakt_door: string;
  aangemaakt_op: string;
}

/** Wat het beheer invult; de rest (nummer, datum, betaalregel) doet de Worker. */
export interface Factuurinvoer {
  bijdrage_id?: string | null;
  ontvanger_naam?: string;
  ontvanger_adres?: string | null;
  ontvanger_email?: string;
  omschrijving?: string;
  bedrag_cent?: number;
}

/** De standaardwaarden voor het factuurformulier. */
export interface Factuurvoorstel {
  ontvanger_naam: string;
  ontvanger_adres: string | null;
  ontvanger_email: string;
  bijdragen: {
    id: string;
    label: string;
    status: Bijdragestatus;
    omschrijving: string;
    bedrag_cent: number;
    /** Nummers van facturen die al voor deze bijdrage zijn gemaakt. */
    gefactureerd: string[];
  }[];
  /** Omschrijving voor een factuur zonder gekoppelde bijdrage. */
  omschrijving_los: string;
}
